import { makeAutoObservable } from 'mobx'

import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { PostPreviewChannelType } from 'components/PostPreviewModal/Types'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { AlmanacFile } from 'types'
import type { PostState } from './types'
import { PostAction } from './hooks/usePostActionService'
import { ExistingScheduledInfo } from './PostPreviewStoreProvider'
import { getSuggestedPostTime } from 'utils/time'

export const PostPreviewScreens = {
  CHANNEL_SCREEN: 'channels',
  PREVIEW_SCREEN: 'preview',
  AUTOPOST_CALENDAR_SCREEN: 'calendar',
  REMINDER_CALENDAR_SCREEN: 'reminderCalendar',
  RESULT_SCREEN: 'result',
  YOUTUBE_SCREEN: 'youtube',
} as const
export type PostPreviewScreen = (typeof PostPreviewScreens)[keyof typeof PostPreviewScreens]

export enum ScheduledFlowMode {
  NEW = 'new',
  EDIT = 'edit',
  CROSSPOST = 'cross',
  WIZARD = 'wizard',
}

export class PostPreviewStore {
  readonly channel?: PostPreviewChannelType
  readonly target: 'text' | 'design'

  selectedAccounts: ExternalIntegration[] = []
  selectedChannels = new Set<ExternalIntegrationEnum>()
  selectedBrandKitId?: number
  postAt: string
  lastAction?: PostAction

  postStates: PostState[] = []

  screen: PostPreviewScreen = PostPreviewScreens.CHANNEL_SCREEN
  history: PostPreviewScreen[] = []

  onFilesCreated?: (files: AlmanacFile[]) => void

  flow: ScheduledFlowMode = ScheduledFlowMode.NEW

  get existingScheduled() {
    return this.flow === ScheduledFlowMode.EDIT
  }

  constructor(opts: {
    channel?: PostPreviewChannelType
    target?: 'text' | 'design'
    selectedBrandKitId?: number
    onFilesCreated?: (files: AlmanacFile[]) => void
    flow?: ScheduledFlowMode
  }) {
    const { channel, target = 'design', selectedBrandKitId, onFilesCreated, flow } = opts

    this.channel = channel
    this.target = target
    this.selectedBrandKitId = selectedBrandKitId
    this.onFilesCreated = onFilesCreated
    this.flow = flow || ScheduledFlowMode.NEW
    this.history.push(PostPreviewScreens.CHANNEL_SCREEN)

    this.postAt = getSuggestedPostTime().toISOString()

    makeAutoObservable(this, { channel: false, target: false }, { autoBind: true })

    if (channel) {
      const provider = providerFromChannelType[channel] as ExternalIntegrationEnum
      this.selectedChannels = new Set([provider])
    }
  }

  initialiseWithExistingScheduledPost(info: ExistingScheduledInfo) {
    if (this.flow !== ScheduledFlowMode.NEW) return

    this.flow = ScheduledFlowMode.EDIT
    this.postAt = info.postAt

    const state: PostState = {
      fileId: String(info.fileId),
      postAt: info.postAt,
      provider: info.provider,
      account: info.externalIntegration,
      runtime: 'success',
      result: 'draft',
      isOriginalFile: true,
    }

    switch (info.status) {
      case PostStatusPropertyValue.AutoPostScheduled:
        state.result = 'autoScheduled'
        break
      case PostStatusPropertyValue.ReminderScheduled:
        state.result = 'reminderScheduled'
        break
      case PostStatusPropertyValue.Posted:
        state.result = 'published'
        break
      case PostStatusPropertyValue.Failed:
        state.runtime = 'failure'
        break
    }

    this.replacePostStates([state])
    this.setScreen(PostPreviewScreens.RESULT_SCREEN)
  }

  startEditSchedule() {
    this.flow = ScheduledFlowMode.EDIT
    this.setScreen(PostPreviewScreens.AUTOPOST_CALENDAR_SCREEN)
  }

  startCrossPost() {
    this.flow = ScheduledFlowMode.CROSSPOST
    this.postStates = []
    this.selectedAccounts = []
    this.setScreen(PostPreviewScreens.CHANNEL_SCREEN)
  }

  setSelectedAccounts(acc: ExternalIntegration[]) {
    this.selectedAccounts = acc
  }
  addChannel(p: ExternalIntegrationEnum) {
    this.selectedChannels.add(p)
  }
  removeChannel(p: ExternalIntegrationEnum) {
    this.selectedChannels.delete(p)
  }
  setBrandKitId(id?: number) {
    this.selectedBrandKitId = id
  }
  setPostAt(iso: string) {
    this.postAt = iso
  }
  setScreen(s: PostPreviewScreen) {
    this.screen = s
    this.history.push(s)
  }
  replacePostStates(v: PostState[]) {
    this.postStates = v
  }
  updatePostState(fileId: string | number, patch: Partial<PostState>) {
    const idx = this.postStates.findIndex((s) => s.fileId === String(fileId))
    if (idx !== -1) this.postStates[idx] = { ...this.postStates[idx], ...patch }
  }
  resetPostStates() {
    this.postStates = []
  }
  setLastAction(a?: PostAction) {
    this.lastAction = a
  }

  goBack() {
    if (this.history.length > 1) {
      this.history.pop()
      const previousScreen = this.history[this.history.length - 1]
      this.screen = previousScreen
    }
  }

  get isPublishing() {
    return this.postStates.some((s) => s.runtime === 'creating' || s.runtime === 'publishing')
  }
  get hasSuccess() {
    return this.postStates.some((s) => s.runtime === 'success')
  }
  get hasFailure() {
    return this.postStates.some((s) => s.runtime === 'failure')
  }
  get canGoBack() {
    return this.history.length > 1
  }
}
